/**
 * Which room of the account the user is standing in.
 *
 * The database already decides what each person may see (RLS asks
 * caller_workspace_role()); this context only remembers which workspace the
 * lists are filtered to and where new clients are filed. Nothing here grants
 * access, so a stale localStorage value can never show anything the server
 * would not have shown.
 *
 * Owners and admins may also stand in "all workspaces" (currentId null),
 * which is simply no filter. Members always stand in one of theirs.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserAccount } from "@/hooks/useUserAccount";

export interface Workspace {
  id: string;
  name: string;
  color: string | null;
  is_default: boolean;
  /** manager | editor | viewer, as the server sees this user in that workspace. */
  role: "manager" | "editor" | "viewer";
  member_count: number;
  client_count: number;
  created_at: string;
}

interface WorkspaceContextValue {
  accountId: string | null;
  workspaces: Workspace[];
  loading: boolean;
  /** null means "all workspaces" (admins only). */
  currentId: string | null;
  current: Workspace | null;
  setCurrentId: (id: string | null) => void;
  /** Where a new client or analysis without a client is filed. */
  targetWorkspaceId: string | null;
  isAccountAdmin: boolean;
  canCreateWorkspaces: boolean;
  /** Plan cap on workspaces; null means unlimited. */
  workspaceLimit: number | null;
  /** May the caller run analyses / create clients in this workspace (or, for null, in any)? Viewers may not. */
  canEdit: (workspaceId?: string | null) => boolean;
  refresh: () => void;
}

const Ctx = createContext<WorkspaceContextValue | null>(null);
const storageKey = (accountId: string) => `ec.workspace.${accountId}`;

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { accountId, role } = useUserAccount();
  const qc = useQueryClient();
  const isAccountAdmin = role === "owner" || role === "editor";

  const { data: workspaces = [], isLoading } = useQuery({
    queryKey: ["workspaces", accountId],
    enabled: !!accountId,
    staleTime: 60_000,
    queryFn: async (): Promise<Workspace[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("my_workspaces", { _account_id: accountId });
      if (error) throw error;
      // The default room was first created as "Team"; it reads as "Default"
      // until the rename migration (20260928090000) has run everywhere.
      return ((data ?? []) as Workspace[]).map((w) => (w.is_default && w.name === "Team" ? { ...w, name: "Default" } : w));
    },
  });

  // Plan gate + who may open a room + how many rooms the plan allows (null = unlimited).
  const { data: creation } = useQuery({
    queryKey: ["workspace-creation", accountId],
    enabled: !!accountId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rpc = supabase.rpc as any;
      const [{ data: feat }, { data: can }, { data: limit }] = await Promise.all([
        rpc("account_has_feature", { p_account_id: accountId, p_feature_key: "workspaces" }),
        rpc("caller_can_create_workspace", { p_account_id: accountId }),
        rpc("workspace_limit", { p_account_id: accountId }),
      ]);
      return { feature: feat === true, can: can === true, limit: (limit ?? null) as number | null };
    },
  });
  // Open with the plan feature, or with extra workspaces granted by an admin (limit above the default one).
  const canCreateWorkspaces = (!!creation?.feature || (creation?.limit ?? 0) > 1) && !!creation?.can;
  const workspaceLimit = creation?.limit ?? null;

  const [currentId, setCurrentIdState] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);

  // Restore the last choice once the list is known; fall back to the default room.
  useEffect(() => {
    if (!accountId || isLoading) return;
    if (restored) return;
    let saved: string | null = null;
    try { saved = localStorage.getItem(storageKey(accountId)); } catch { /* private mode */ }
    if (saved === "all" && isAccountAdmin) { setCurrentIdState(null); setRestored(true); return; }
    const found = workspaces.find((w) => w.id === saved);
    if (found) setCurrentIdState(found.id);
    else if (workspaces.length === 1) setCurrentIdState(workspaces[0].id);
    else if (isAccountAdmin) setCurrentIdState(null);
    else setCurrentIdState(workspaces.find((w) => w.is_default)?.id ?? workspaces[0]?.id ?? null);
    setRestored(true);
  }, [accountId, isLoading, workspaces, restored, isAccountAdmin]);

  // A workspace that was deleted while selected: fall back rather than filter to nothing.
  useEffect(() => {
    if (!restored || currentId === null) return;
    if (!isLoading && workspaces.length > 0 && !workspaces.some((w) => w.id === currentId)) {
      setCurrentIdState(isAccountAdmin ? null : workspaces[0].id);
    }
  }, [workspaces, isLoading, currentId, restored, isAccountAdmin]);

  const setCurrentId = useCallback((id: string | null) => {
    setCurrentIdState(id);
    if (accountId) { try { localStorage.setItem(storageKey(accountId), id ?? "all"); } catch { /* ignore */ } }
  }, [accountId]);

  const refresh = useCallback(() => { void qc.invalidateQueries({ queryKey: ["workspaces", accountId] }); }, [qc, accountId]);

  const value = useMemo<WorkspaceContextValue>(() => {
    const current = workspaces.find((w) => w.id === currentId) ?? null;
    const target = current?.id ?? workspaces.find((w) => w.is_default)?.id ?? workspaces[0]?.id ?? null;
    const canEdit = (id?: string | null) => {
      if (isAccountAdmin) return true;
      const w = id ? workspaces.find((x) => x.id === id) : (current ?? null);
      if (w) return w.role === "manager" || w.role === "editor";
      return workspaces.some((x) => x.role === "manager" || x.role === "editor");
    };
    return {
      accountId, workspaces, loading: isLoading || !restored, currentId, current, setCurrentId,
      targetWorkspaceId: target, isAccountAdmin, canCreateWorkspaces, workspaceLimit, canEdit, refresh,
    };
  }, [accountId, workspaces, isLoading, restored, currentId, setCurrentId, isAccountAdmin, canCreateWorkspaces, workspaceLimit, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const EMPTY: WorkspaceContextValue = {
  accountId: null, workspaces: [], loading: false, currentId: null, current: null, setCurrentId: () => {},
  targetWorkspaceId: null, isAccountAdmin: false, canCreateWorkspaces: false, workspaceLimit: null, canEdit: () => true, refresh: () => {},
};

/** Safe outside the provider (public pages): behaves as "no workspaces". */
export function useWorkspace(): WorkspaceContextValue {
  return useContext(Ctx) ?? EMPTY;
}

export const WORKSPACE_ROLE_LABEL: Record<Workspace["role"], { el: string; en: string }> = {
  manager: { el: "Διαχειριστής", en: "Manager" },
  editor: { el: "Συντάκτης", en: "Editor" },
  viewer: { el: "Θεατής", en: "Viewer" },
};

export const ACCOUNT_ROLE_LABEL: Record<string, { el: string; en: string }> = {
  owner: { el: "Ιδιοκτήτης", en: "Owner" },
  editor: { el: "Admin", en: "Admin" },
  user: { el: "Μέλος", en: "Member" },
};
